import { useRef } from 'react';

import {
  Box,
  Button,
  Typography,
  NativeSelect,
  OutlinedInput,
  Stack,
  TextField,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useLocation, useNavigate } from 'react-router-dom';

import { Ti18nId } from 'hooks/useI18n';
import { setSearchQuery } from 'utils/searchQuery';
import { TApprove } from './api/type';

import { formatWithComma } from 'utils/formatting';
import { IAdminPatients } from './api/type';
import useNotification from 'hooks/useNotification';

import { getAccountsPatient, postPrivate, postPublic } from './api/index';
import useUser from 'store/user/useUser';

interface Props {
  totalCount: number;
  onRequired: (id: Ti18nId) => void;
  currentSearchType?: number;
  currentApprove: string | TApprove | null;
  selected: string[];
  list: IAdminPatients[];
  handleAddFriend: () => void;
  setList: React.Dispatch<React.SetStateAction<IAdminPatients[]>>;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const ListHeader = ({
  totalCount,
  onRequired,
  currentSearchType,
  currentApprove,
  list,
  selected,
  handleAddFriend,
  setList,
  setTotalCount,
  setIsLoading,
}: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { onSuccess, onFail, onResultCode } = useNotification();
  const { student_uuid: userId } = useUser();

  const searchTypeRef = useRef<HTMLSelectElement>(null);
  const keywordRef = useRef<HTMLInputElement>(null);

  const onSearch = () => {
    const keyword = keywordRef.current?.value;
    const patient_id = Number(keywordRef.current?.value);
    const college = Number(keywordRef.current?.value);

    const searchType = searchTypeRef.current?.value;
    if (!keyword) return onRequired('REQUIRED.KEYWORD');

    if (searchType === '2' && isNaN(Number(keyword)))
      return onRequired('REQUIRED.NUMBER');
    if (searchType === '1' && !isNaN(Number(keyword)))
      return onRequired('REQUIRED.STRING');

    const query = isNaN(Number(keyword))
      ? setSearchQuery({
          page: 1,
          approve: currentApprove,
          searchType,
          keyword,
        })
      : searchType === '2'
      ? setSearchQuery({
          page: 1,
          approve: currentApprove,
          searchType,
          keyword: patient_id,
        })
      : setSearchQuery({
          page: 1,
          approve: currentApprove,
          searchType,
          patient_id: '',
          keyword: '',
          college,
        });
    navigate(`${pathname}?${query}`);
  };

  const searchReset = () => {
    getAccountsPatient({
      page: '1',

      keyword: '',
      searchType: '2',
      user_id: String(userId),
    })
      .then(({ data }) => {
        setList(data.patients);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `가상환자 목록을 불러오는데 실패했습니다.`;
        onFail(message, e);
      })
      .finally(() => setIsLoading(false));

    navigate('/professorAuth/list');
  };

  const rows = list.map(item => ({ ...item, id: item.patient_id }));

  const changeNumber = selected.map(a => Number(a));

  const selectedIDs = new Set(changeNumber);
  const selectedRows = rows.filter(r => selectedIDs.has(r.id));

  const publicButton = () => {
    const 비공개환자 = selectedRows.map(a => a.patient_private).filter(a => a);
    const 공개환자 = selectedRows.map(a => a.patient_private).filter(a => !a);

    // 선택자가 전부 공개인경우
    if (공개환자.length > 0) {
      return alert('미공개 환자들만 선택해주세요.');
    }
    // 선택자가 전부 비공개인 경우
    if (비공개환자.length > 0) {
      let selectedPublic = selectedRows
        .filter(r => r.patient_private)
        .map(a => a.patient_id);

      postPublic({
        patient_id: selectedPublic.join(','),
        user_id: userId,
        setType: 'public',
      })
        .then(({ data: { rc } }) => {
          if (rc === 111) {
            return onResultCode(rc);
          }

          const message = `${selected.length}개의 가상환자가 학생 ENR 공개처리 되었습니다.`;
          onSuccess(message);
          handleAddFriend();
        })
        .catch(e => {
          const message = `${selected.length}개의 가상환자가 학생 ENR 공개처리에 실패했습니다.`;
          onFail(message, e);
        });
    }
  };

  const privateButton = () => {
    const 비공개 = selectedRows.map(a => a.patient_private).filter(a => a);
    const 공개 = selectedRows.map(a => a.patient_private).filter(a => !a);

    // 선택자가 전부 공개인경우
    if (공개.length > 0) {
      let selectedPublic = selectedRows
        .filter(r => !r.patient_private)
        .map(a => a.patient_id);

      postPublic({
        patient_id: selectedPublic.join(','),
        user_id: userId,
        setType: 'private',
      })
        .then(({ data: { rc } }) => {
          if (rc === 111) {
            return onResultCode(rc);
          }

          const message = `${selected.length}개의 가상환자가 학생 ENR 비공개처리 되었습니다.`;
          onSuccess(message);
          handleAddFriend();
        })
        .catch(e => {
          const message = `${selected.length}개의 가상환자가 학생 ENR 비공개처리에 실패했습니다.`;
          onFail(message, e);
        });
    }
    // 선택자가 전부 비공개인 경우
    if (비공개.length > 0) {
      return alert('공개 환자들만 선택해주세요.');
    }
  };

  const ActionButton = () => (
    <Stack direction={'row'} spacing={2}>
      <LoadingButton variant="contained" onClick={() => publicButton()}>
        학생 ENR 공개
      </LoadingButton>
      <LoadingButton
        variant="contained"
        color="error"
        sx={{ backgroundColor: '#E63529!important' }}
        onClick={() => privateButton()}
      >
        학생 ENR 비공개
      </LoadingButton>
    </Stack>
  );

  const SearchKeyword = () => (
    <Stack direction={'row'} spacing={0.5} alignItems="center">
      <NativeSelect
        size="small"
        input={<OutlinedInput />}
        inputRef={searchTypeRef}
        defaultValue={currentSearchType}
      >
        <option value={1}>이름</option>
        <option value={2}>아이디</option>
      </NativeSelect>
      <TextField
        label="검색"
        size="small"
        inputRef={keywordRef}
        sx={{ input: { minHeight: 40, boxSizing: 'border-box' } }}
      />
      <Button variant="contained" onClick={onSearch}>
        검색
      </Button>
      <Button variant="contained" onClick={searchReset}>
        검색 초기화
      </Button>
    </Stack>
  );

  return (
    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mr: 'auto' }}>
        총 {formatWithComma(totalCount)}명
      </Typography>
      <Stack direction="row" spacing={2}>
        <ActionButton />
        <Divider sx={{ borderColor: 'gray' }} />
        <SearchKeyword />
      </Stack>
    </Box>
  );
};

export default ListHeader;
